import React, { useState } from 'react';
import RegisterDetailsSidebar from './SignUpSidebar';
import RegisterPasswordSidebar from './SignupPasswordSidebar';
import RegisterSetPasswordForm from './Forms/SetPassword';
import RegisterDetailsForm from './Forms/RegisterDetails';
import { useAuth } from '../../AuthContextProvider';
import Button from '../../../Button';
import { useForm } from 'react-hook-form';
import { AuthValidationError } from '../../types';
import { toast } from 'react-toastify';

export interface RegisterFormInputs {
  name: string;
  email: string;
  password: string;
  password_confirmation: string;
}

type InputErrorKey = keyof RegisterFormInputs;

const RegisterScreen: React.FC<any> = () => {
  const { actions, registrationEnabled } = useAuth();

  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<'details' | 'password'>('details');

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    watch,
    setError,
    getValues,
  } = useForm<RegisterFormInputs>({
    defaultValues: {
      name: '',
      email: '',
      password: '',
      password_confirmation: '',
    },
    reValidateMode: 'onBlur',
  });

  const [passwordStrength, setPasswordStrength] = useState({
    characters: false,
    uppercase: false,
    special: false,
    number: false,
    match: false,
  });

  const validateDetails = () => {
    setStep('password');
  };

  const onFinish = async (values: RegisterFormInputs) => {
    setLoading(true);

    try {
      await actions.emailRegister(
        values.name,
        values.email,
        values.password,
        values.password_confirmation
      );
    } catch (error) {
      if (error instanceof AuthValidationError) {
        toast(error.message, { type: 'error' });

        const errors = error.errors;
        const keys = Object.keys(errors) as InputErrorKey[];

        keys.forEach(errKey => {
          // @ts-ignore
          setError(errKey, { type: 'api', message: errors[errKey] as string });
        });

        if (keys.includes('email') || keys.includes('name')) {
          setStep('details');
        }
      } else {
        console.error(error);
      }
    }
    setLoading(false);
  };

  if (!registrationEnabled)
    return (
      <div className="text-center">
        <h2 className="text-3xl mb-8">
          Registration is currently disabled while we are in beta, please
          contact us to request beta access.
        </h2>
        <Button text={'Back Home'} buttonStyle="gradiented" isLink href="/" />
      </div>
    );

  return (
    <div className="min-h-screen flex justify-between items-center w-full">
      {step === 'details' && <RegisterDetailsSidebar />}
      {step === 'password' && (
        <RegisterPasswordSidebar passwordStrength={passwordStrength} />
      )}

      <form
        onSubmit={handleSubmit(onFinish)}
        className="min-h-screen flex flex-col justify-center items-center py-12 sm:px-6 lg:px-8 w-full"
      >
        {step === 'details' && (
          <RegisterDetailsForm
            loading={loading}
            validateAndNext={validateDetails}
            formProps={{ control, errors, getValues }}
          />
        )}
        {step === 'password' && (
          <RegisterSetPasswordForm
            loading={loading}
            setPasswordStrength={setPasswordStrength}
            backStep={() => setStep('details')}
            formProps={{ control, errors, watch, register }}
          />
        )}
      </form>
    </div>
  );
};

export default RegisterScreen;
